import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { fabric } from 'fabric';
import TextboxIcon from '../images/Icon20_Text.svg';
import {
  setCursorTooltip,
  updateTool,
} from '../../../redux/actions/canvas_editor';
import Button from './Toolbar/Button';
import { connect } from 'react-redux';

import { getThicknessPx, getDrawingBounds } from '../helpers/fabricHelpers';

const TEXTBOX_DEFAULT_WIDTH = 500;
const TEXTBOX_MIN_WIDTH = 100;
const MIN_TEXTBOX_HEIGHT_MULTIPLIER = 2;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const Textbox = ({
  canvas_editor: { viewer, color, fabricCanvases, selectedThickness },
  isActive,
  user_id,
  user_full_name,
  source: { width, height },
  sourceType = 'osd',
}) => {
  const colorRef = useRef(color);
  const thicknessRef = useRef(selectedThickness);
  colorRef.current = color;
  thicknessRef.current = selectedThickness;

  useEffect(() => {
    if (!isActive || !fabricCanvases) return;

    setCursorTooltip('Click to place text');

    const disposers = [];

    Object.entries(fabricCanvases).forEach(([pageNumber, canvas]) => {
      canvas.selection = false;
      canvas.defaultCursor = 'crosshair';
      //canvas.hoverCursor = 'crosshair';

      canvas.getObjects().forEach((obj) => {
        obj.selectable = false;
        obj.evented = false;
      });

      const onPointerDown = () => {
        viewer?.setMouseNavEnabled(false);
      };

      const focusTextboxForEditing = (textbox) => {
        window.setTimeout(() => {
          if (!textbox || !canvas.getObjects().includes(textbox)) return;

          viewer?.setMouseNavEnabled(false);
          canvas.setActiveObject(textbox);
          canvas.requestRenderAll();

          if (!textbox.isEditing && textbox.editable) {
            textbox.enterEditing();
          }

          textbox.hiddenTextarea?.focus();
        }, 0);
      };

      const onMouseDown = (options) => {
        if (!isActive || options.target) return;

        const pdfScaleFactor =
          sourceType === 'pdf' ? canvas?.pdfScaleFactor || 1 : 1;
        const fontSize =
          getThicknessPx(thicknessRef.current?.key, 'textbox') * pdfScaleFactor;

        const rawPointer = canvas.getPointer(options.e);
        const { width: canvasWidth, height: canvasHeight } = getDrawingBounds(
          canvas,
          { width, height },
          sourceType
        );

        const pointer = {
          x: clamp(rawPointer.x, 0, canvasWidth),
          y: clamp(rawPointer.y, 0, canvasHeight),
        };
        const safeLeft = clamp(
          pointer.x,
          0,
          Math.max(0, canvasWidth - TEXTBOX_MIN_WIDTH)
        );
        const safeTop = clamp(
          pointer.y,
          0,
          Math.max(0, canvasHeight - fontSize * MIN_TEXTBOX_HEIGHT_MULTIPLIER)
        );
        const textboxWidth = clamp(
          sourceType == 'pdf'
            ? TEXTBOX_DEFAULT_WIDTH * 0.35
            : TEXTBOX_DEFAULT_WIDTH,
          TEXTBOX_MIN_WIDTH,
          Math.max(TEXTBOX_MIN_WIDTH, canvasWidth - safeLeft)
        );

        // const maxWidth = Math.max(TEXTBOX_MIN_WIDTH, width - safeLeft);

        // const textbox_width =
        //   sourceType == 'pdf'
        //     ? TEXTBOX_DEFAULT_WIDTH * 0.35
        //     : TEXTBOX_DEFAULT_WIDTH;

        const textbox = new fabric.Textbox('', {
          left: safeLeft,
          top: safeTop,
          width: textboxWidth,
          //minWidth: TEXTBOX_MIN_WIDTH,
          splitByGrapheme: true,
          fontSize,
          lineHeight: 1.2,
          fill: colorRef.current?.hex || color.hex,
          editable: true,
          objectType: 'textbox',
          hasBorders: true,
          hasControls: false,
          lockScalingY: true,
          userID: user_id,
          userFullName: user_full_name,
          fontFamily: 'Arial, Helvetica, sans-serif',
          pageNumber,
        });

        canvas.add(textbox);
        canvas.setActiveObject(textbox);
        canvas.requestRenderAll();

        updateTool({ tool: 'select' });
        focusTextboxForEditing(textbox);
      };

      canvas.upperCanvasEl.addEventListener('pointerdown', onPointerDown);
      canvas.on('mouse:down', onMouseDown);

      disposers.push(() => {
        if (canvas?.upperCanvasEl) {
          canvas.upperCanvasEl.removeEventListener(
            'pointerdown',
            onPointerDown
          );
        }

        canvas.off('mouse:down', onMouseDown);
      });
    });

    return () => {
      disposers.forEach((dispose) => dispose());
      //viewer?.setMouseNavEnabled(true);
    };
  }, [
    isActive,
    viewer,
    fabricCanvases,
    user_id,
    user_full_name,
    width,
    height,
    sourceType,
  ]);

  const toggleTool = () => {
    updateTool({ tool: 'textbox' });
  };

  return (
    <Button
      id='textbox'
      label='Text'
      icon={TextboxIcon}
      active={isActive}
      onClick={toggleTool}
    />
  );
};

Textbox.propTypes = {
  isActive: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  canvas_editor: state.canvas_editor,
  user_id: state.auth.id,
  user_full_name: state.auth.firstname + ' ' + state.auth.lastname,
});

export default connect(mapStateToProps, null)(Textbox);
